/**
 * Paper panel that reveals the family letter paragraph by paragraph with a brush-like typewriter
 * (slower cadence, longer breath at punctuation, an ink cursor riding the last stroke). Turns the
 * page when the next paragraph would not fit. Used by the prologue's `letter` timeline steps.
 */
import Phaser from 'phaser';
import { Events } from '@/core/events';
import type { UiRoot } from './UiRoot';
import { makeMeasurer, uiText } from './text';
import { Css, Palette, Slices, UiDepth } from './theme';
import { Typewriter, type TextSpeed } from './typewriter';
import { wrapText } from './wrap';
import { FONT_TITLE } from '@/core/config';

export interface LetterParagraphOptions { instant?: boolean }

const PANEL = { x: 110, y: 20, w: 420, h: 208 } as const;
const PAD = 24;
const LINE_H = 22;
const STYLE: Phaser.Types.GameObjects.Text.TextStyle = { fontFamily: FONT_TITLE, fontSize: '16px', fontStyle: 'bold', color: Css.ink, lineSpacing: LINE_H - 16 - 4 };

interface Paragraph { text: Phaser.GameObjects.Text; lines: string[]; y: number; highlight?: Phaser.GameObjects.Rectangle }

export class LetterPanel {
  readonly container: Phaser.GameObjects.Container;
  private readonly paper: Phaser.GameObjects.NineSlice;
  private readonly cursor: Phaser.GameObjects.Rectangle;
  private readonly measurer: ReturnType<typeof makeMeasurer>;
  private readonly pageLabel: Phaser.GameObjects.Text;
  private paragraphs: Paragraph[] = [];
  private nextY = PANEL.y + PAD;
  private page = 1;
  private typer: Typewriter | null = null;
  private typing: { para: Paragraph; resolve: () => void } | null = null;
  private _open = false;

  constructor(private readonly root: UiRoot) {
    const s = root.scene;
    this.container = s.add.container(0, 0).setDepth(UiDepth.LETTER).setVisible(false).setAlpha(0);
    this.paper = s.add.nineslice(PANEL.x, PANEL.y, Slices.paper.key, undefined, PANEL.w, PANEL.h, Slices.paper.l, Slices.paper.r, Slices.paper.t, Slices.paper.b).setOrigin(0);
    // A faint seal mark in the corner (dev placeholder, lacquer square).
    const seal = s.add.rectangle(PANEL.x + PANEL.w - 18, PANEL.y + PANEL.h - 18, 10, 10, Palette.lacquer, 0.5).setOrigin(0.5);
    this.pageLabel = uiText(s, PANEL.x + PANEL.w / 2, PANEL.y + PANEL.h - 11, '', { fontSize: '10px', color: Css.inkSoft }).setOrigin(0.5);
    this.cursor = s.add.rectangle(0, 0, 3, 14, Palette.ink, 0.9).setOrigin(0, 0).setVisible(false);
    this.container.add([this.paper, seal, this.pageLabel, this.cursor]);
    this.measurer = makeMeasurer(s, STYLE);
    root.onUpdate((_t, d) => this.tick(d));
    root.onBus(Events.INPUT_CONFIRM, () => this.skip());
    root.track(() => this.teardown());
  }

  isOpen(): boolean { return this._open; }
  isTyping(): boolean { return this.typer !== null && !this.typer.done; }

  open(): Promise<void> {
    this.clearParagraphs();
    this.page = 1;
    this.pageLabel.setText('· 一 ·');
    this._open = true;
    const s = this.root.scene;
    this.container.setVisible(true).setAlpha(0).setY(this.root.reduceMotion() ? 0 : 14);
    return new Promise((resolve) => {
      s.tweens.killTweensOf(this.container);
      s.tweens.add({ targets: this.container, alpha: 1, y: 0, duration: this.root.reduceMotion() ? 0 : 260, ease: 'Sine.easeOut', onComplete: () => resolve() });
    });
  }

  /** Reveals a paragraph; resolves when fully written. Turns the page when it would not fit. */
  async addParagraph(text: string, opts: LetterParagraphOptions = {}): Promise<void> {
    if (!this._open) await this.open();
    this.skip();
    const width = PANEL.w - PAD * 2;
    const lines = wrapText(text, width, this.measurer.measure);
    const height = lines.length * LINE_H;
    if (this.nextY + height > PANEL.y + PANEL.h - PAD - 4 && this.paragraphs.length > 0) await this.turnPage();
    const s = this.root.scene;
    const t = uiText(s, PANEL.x + PAD, this.nextY, '', STYLE);
    this.container.add(t);
    const para: Paragraph = { text: t, lines, y: this.nextY };
    this.paragraphs.push(para);
    this.nextY += height + 8;
    const speed: TextSpeed = opts.instant ? 'instant' : (this.root.ctx?.settings.textSpeed ?? 'normal');
    this.typer = new Typewriter(lines.join('\n'), speed, { multiplier: 1.5, pauseScale: 1.3 });
    t.setText(this.typer.text);
    return new Promise((resolve) => {
      this.typing = { para, resolve };
      if (this.typer?.done) this.finish();
      else this.placeCursor(para);
    });
  }

  /** Marks the most recent paragraph (e.g. the line the memory stage will branch from). */
  highlightLast(): void {
    const para = this.paragraphs[this.paragraphs.length - 1];
    if (!para) return;
    if (this.typer && !this.typer.done) this.skip();
    const s = this.root.scene;
    para.text.setColor(Css.lacquer);
    para.highlight?.destroy();
    const h = para.lines.length * LINE_H;
    const rect = s.add.rectangle(PANEL.x + PAD - 6, para.y - 2, PANEL.w - PAD * 2 + 12, h + 2, Palette.lantern, 0).setOrigin(0);
    this.container.addAt(rect, 1);
    para.highlight = rect;
    s.tweens.add({ targets: rect, fillAlpha: 0.28, duration: this.root.reduceMotion() ? 0 : 300 });
  }

  /** Completes the current paragraph immediately. */
  skip(): void {
    if (this.typer && !this.typer.done) { this.typer.complete(); this.finish(); }
  }

  close(): Promise<void> {
    this.skip();
    if (!this._open) return Promise.resolve();
    this._open = false;
    const s = this.root.scene;
    return new Promise((resolve) => {
      s.tweens.killTweensOf(this.container);
      s.tweens.add({ targets: this.container, alpha: 0, duration: this.root.reduceMotion() ? 0 : 220, onComplete: () => { if (!this._open) { this.container.setVisible(false); this.clearParagraphs(); } resolve(); } });
    });
  }

  // ------------------------------------------------------------------ internals
  private tick(delta: number): void {
    if (!this.typer || this.typer.done || !this.typing) return;
    if (this.root.hasModal()) return;
    if (this.typer.tick(Math.min(delta, 100))) {
      this.typing.para.text.setText(this.typer.text);
      if (this.typer.done) this.finish(); else this.placeCursor(this.typing.para);
    }
  }

  private placeCursor(para: Paragraph): void {
    if (!this.typer) return;
    const shown = this.typer.text.split('\n');
    const lineIdx = Math.max(0, shown.length - 1);
    const lastLine = shown[lineIdx] ?? '';
    const x = PANEL.x + PAD + Math.round(this.measurer.measure(lastLine)) + 1;
    const y = para.y + lineIdx * LINE_H + 3;
    this.cursor.setPosition(x, y).setVisible(true);
    this.container.bringToTop(this.cursor);
  }

  private finish(): void {
    const t = this.typing;
    this.typing = null;
    if (t && this.typer) t.para.text.setText(this.typer.text);
    this.cursor.setVisible(false);
    t?.resolve();
  }

  private turnPage(): Promise<void> {
    const s = this.root.scene;
    const olds = this.paragraphs.splice(0);
    this.page++;
    this.pageLabel.setText(`· ${'一二三四五六七八九十'[Math.min(9, this.page - 1)] ?? this.page} ·`);
    this.nextY = PANEL.y + PAD;
    const targets = olds.flatMap((p) => (p.highlight ? [p.text, p.highlight] : [p.text]));
    if (this.root.reduceMotion() || targets.length === 0) { for (const p of olds) { p.text.destroy(); p.highlight?.destroy(); } return Promise.resolve(); }
    return new Promise((resolve) => {
      s.tweens.add({ targets, alpha: 0, duration: 260, onComplete: () => { for (const p of olds) { p.text.destroy(); p.highlight?.destroy(); } resolve(); } });
    });
  }

  private clearParagraphs(): void {
    for (const p of this.paragraphs) { p.text.destroy(); p.highlight?.destroy(); }
    this.paragraphs = [];
    this.nextY = PANEL.y + PAD;
    this.typer = null;
    const t = this.typing; this.typing = null; t?.resolve();
    this.cursor.setVisible(false);
  }

  private teardown(): void {
    this.clearParagraphs();
    this.measurer.destroy();
    this.container.destroy(true);
  }
}
